import { StringUtils } from 'src/app/shared/common/utility/string-utils';
import { Feature, IFeature, IFeatureDTO } from 'src/app/shared/map-data-services/feature/feature';
import {
    AutoField,
    AutoFieldModelType,
    BarcodeField,
    ChoiceField,
    CodedChoiceField,
    DateField,
    Field,
    FieldType,
    GroupField,
    LayoutFieldType,
    NumberField,
    TextField
} from 'src/app/shared/template-services/field';
import { Template } from 'src/app/shared/template-services/template';

import { TranslationService } from 'src/app/core/translation/translation.service';
import { ProjectStreamService } from 'src/app/shared/common/current-project/project-stream.service';
import {
    AutoFeatureField,
    BarcodeFeatureField,
    ChoiceFeatureField,
    CodedChoiceFeatureField,
    DateFeatureField,
    FeatureField,
    GroupFeatureField,
    ImageSignatureFeatureField,
    NumberFeatureField,
    PageHeaderFeatureField,
    TextFeatureField,
    YesNoFeatureField
} from './feature-field';

export enum TemplatedFeatureMetadataProperty {
    // Shared metadata fields - TODO: There are more to be added here
    COLLECTION_COLLECTED_BY = 'collection_collectedBy',
    COLLECTION_UPDATED_BY = 'collection_updatedBy',
    COLLECTION_WORKSPACE_ID = 'collection_workspaceid',
    COLLECTION_UTC = 'collection_utc',
    COMMON_LAYER_ID = 'common_layerId',
    COMMON_UPDATED_UTC = 'common_updatedUtc',
    COLLECTION_SYNC_DATE = 'collection_syncDate',
    GEOMETRY_TYPE = 'geometry_type',

    // GIS - Metadata fields
    POST_PROCESSED_STATUS = 'processing_postprocessedStatus',
    PROCESSING_ROVER_SESSION_ID = 'processing_roverSessionId', // used for reprocessing
    COMMON_UNPROCESSED = 'common_unprocessed',
    COMMON_PROCESSED = 'common_processed',
    COLLECTION_ORIGINAL_UTC = 'collection_original_utc',
    FILE_CONNECT_FILE_ID = 'file_connectFileId',
    FILE_CONNECT_VERSION_ID = 'file_connectVersionId',
    FILE_SOURCE_LAYER = 'file_sourceLayer',
    WORKSPACE_ID = 'workspaceId',
    IMPORT_ID = 'importId',
    COLLECTION_USER_INFO = 'collection_userinfo',
    COLLECTION_TODO_ID = 'collection_todoid',
    COLLECTION_GEOMETRY_UTC = 'collection_geometry_utc',
    COMMON_LOCATION = 'common_location',

    // Survey - Metadata fields
    COMMON_JOB_ID = 'common_jobId',
    SURVEY_NAME = 'survey_name',
    SURVEY_CATEGORY = 'survey_category',
    SURVEY_METHOD = 'survey_method',
    SURVEY_CLASSIFICATION = 'survey_classification',
    SURVEY_FEATURE_NAME = 'survey_featureName',
    SURVEY_FEATURE_CODE = 'survey_featureCode',
    SURVEY_XPOSITION = 'survey_xposition',
    SURVEY_YPOSITION = 'survey_yposition',
    SURVEY_ZPOSITION = 'survey_zposition',
    COLLECTION_DEVICE_INFO = 'collection_deviceInfo'
}

/**
 * Metadata field definition with display name and field type
 * Used to properly render survey metadata in the feature panel
 */
export interface SurveyFieldDef {
    displayName: string;
    fieldType: FieldType;
}

// TODO: translate display names - Category, Method, Classification, Feature Name, Feature Code, Collected By, Device Info
/**
 * Standard metadata key mappings for survey features
 * Maps raw metadata keys to display names and appropriate field types
 */
export const SurveyMetadataFieldDefMap: Partial<Record<TemplatedFeatureMetadataProperty, Partial<SurveyFieldDef>>> = {
    [TemplatedFeatureMetadataProperty.SURVEY_NAME]: { displayName: 'TC.Common.Name', fieldType: FieldType.Text },
    [TemplatedFeatureMetadataProperty.SURVEY_CATEGORY]: {
        displayName: 'Survey.FeaturePanel.FieldLabel.Category'
    },
    [TemplatedFeatureMetadataProperty.SURVEY_METHOD]: {
        displayName: 'Survey.FeaturePanel.FieldLabel.Method'
    },
    [TemplatedFeatureMetadataProperty.SURVEY_CLASSIFICATION]: {
        displayName: 'Survey.FeaturePanel.FieldLabel.Classification'
    },
    [TemplatedFeatureMetadataProperty.SURVEY_FEATURE_NAME]: {
        displayName: 'Survey.FeaturePanel.FieldLabel.FeatureName'
    },
    [TemplatedFeatureMetadataProperty.SURVEY_FEATURE_CODE]: {
        displayName: 'Survey.FeaturePanel.FieldLabel.FeatureCode'
    },
    [TemplatedFeatureMetadataProperty.SURVEY_XPOSITION]: { displayName: 'TCS.XPosition', fieldType: FieldType.Number },
    [TemplatedFeatureMetadataProperty.SURVEY_YPOSITION]: { displayName: 'TCS.YPosition', fieldType: FieldType.Number },
    [TemplatedFeatureMetadataProperty.SURVEY_ZPOSITION]: { displayName: 'TCS.ZPosition', fieldType: FieldType.Number },
    [TemplatedFeatureMetadataProperty.COLLECTION_COLLECTED_BY]: {
        displayName: 'Survey.FeaturePanel.FieldLabel.CollectedBy'
    },
    [TemplatedFeatureMetadataProperty.COLLECTION_UTC]: {
        displayName: 'Survey.FeaturePanel.FieldLabel.CollectedAt'
    },
    [TemplatedFeatureMetadataProperty.COLLECTION_DEVICE_INFO]: {
        displayName: 'Survey.FeaturePanel.FieldLabel.DeviceInfo'
    }
};

// A feature instance, decorated with some template information, for display in the feature side-panel
export class TemplatedFeature implements IFeature {
    id: string;
    metadata: { [key: string]: any };
    properties: { [key: string]: any };
    symbologyProperties: { [key: string]: any };
    colorKey: string;
    geometryType: string;
    geometry: any;
    tags: string[];
    type: string;
    layerName: string;
    isLayerRemoved: boolean;
    template_name: string;
    fields: FeatureField[];
    totalPages: number;
    pageTabCount: number;

    fieldCount = 0;

    // Model/Constructor
    constructor(
        feature: Feature,
        template: Template,
        private services?: { projectStream?: ProjectStreamService; translate?: TranslationService }
    ) {
        this.services = this.services || {};
        this.metadata = feature.metadata;
        this.properties = feature.properties;
        this.id = feature.id;
        this.colorKey = feature.colorKey;
        this.geometryType = feature.geometryType;
        this.geometry = feature.geometry;
        this.type = 'Feature';
        this.layerName = feature.layerName;
        this.isLayerRemoved = feature.isLayerRemoved;
        this.pageTabCount = 0;

        this.template_name = template.name;

        let pageCount = 0;
        this.fields = [];
        let groupId: string = null;
        const groups: { [key: string]: { displayName: string; uuid: string } } = {};

        template.groups.forEach(group => (groups[group.uuid] = group));

        let templateFields: FeatureField[] = [];

        if (feature.metadata?.common_jobId) {
            // Survey features - fields set externally via SurveyTemplatedFieldService - we skip traditional templateField population and use `createFeatureField()`
            return;
        }

        // Gis - TemplatedFeature Fields
        template.fields.forEach(field => {
            const featureField = this.createFeatureField(feature, field.toDTO());
            if (field.groupId) {
                if (groupId !== field.groupId) {
                    groupId = field.groupId;
                    templateFields.push({
                        type: LayoutFieldType.Group,
                        id: Number(field.groupId),
                        displayName: groups[field.groupId].displayName
                    } as FeatureField);
                }
            }

            templateFields.push(featureField);

            if (featureField.type === LayoutFieldType.PageHeader) {
                (featureField as PageHeaderFeatureField).pageCount = ++pageCount;
            }
        });

        this.fields = templateFields;
        this.totalPages = pageCount;
    }

    toDTO(): IFeatureDTO {
        // to API request DTO
        return {
            id: this.id,
            metadata: this.metadata,
            properties: this.properties,
            tags: this.tags
        };
    }

    createFeatureField(feature: Feature, templateField: Field): FeatureField {
        const fieldValue: any =
            undefined === typeof feature.properties[templateField.name]
                ? feature.properties[templateField.name.toLowerCase()]
                : feature.properties[templateField.name];

        const featureField: FeatureField = {
            id: this.fieldCount++,
            uuid: templateField.uuid,
            name: templateField.name,
            displayName: templateField.displayName,
            type: templateField.type,
            groupId: templateField.groupId,
            required: templateField.required,
            value: null,
            hasError: false
        };

        switch (templateField.type) {
            case LayoutFieldType.Group:
                let groupFeatureField = featureField as GroupFeatureField;
                let groupTemplateField = templateField as GroupField;
                groupFeatureField.fields = [];
                if (groupTemplateField.fields) {
                    groupTemplateField.fields.forEach(f => {
                        const f2 = this.createFeatureField(feature, f.toDTO());
                        groupFeatureField.fields.push(f2);
                    });
                }
                break;

            case FieldType.Image:
            case FieldType.Signature:
                let imageSignatureFeatureField = featureField as ImageSignatureFeatureField;
                if (fieldValue && StringUtils.isString(fieldValue)) {
                    const images: string[] = fieldValue.split('|');
                    imageSignatureFeatureField.value = images;
                }
                break;

            case FieldType.Date:
                let dateFeatureField = featureField as DateFeatureField;
                let dateTemplateField = templateField as DateField;
                dateFeatureField.value = fieldValue;
                dateFeatureField.minimum = dateTemplateField.minimum as Date;
                dateFeatureField.maximum = dateTemplateField.maximum as Date;
                break;

            case FieldType.Number:
            case FieldType.Length:
            case FieldType.Angle:
                let numberFeatureField = featureField as NumberFeatureField;
                let numberTemplateField = templateField as NumberField;
                numberFeatureField.value = fieldValue;
                numberFeatureField.scale = numberTemplateField.scale;
                numberFeatureField.minimum = numberTemplateField.minimum as number;
                numberFeatureField.maximum = numberTemplateField.maximum as number;
                numberFeatureField.numberType = numberTemplateField.numberType;
                numberFeatureField.angleUnit = numberTemplateField.angleUnit;
                numberFeatureField.distanceUnit = numberTemplateField.distanceUnit;
                break;

            case FieldType.Text:
                let textFeatureField = featureField as TextFeatureField;
                let textTemplateField = templateField as TextField;
                textFeatureField.value = fieldValue;
                textFeatureField.maxLength = textTemplateField.length;
                break;

            case FieldType.Autofield:
            case AutoFieldModelType.GeometryArea:
            case AutoFieldModelType.GeometryLength:
            case AutoFieldModelType.EstimatedVerticalPrecision:
            case AutoFieldModelType.EstimatedAccuracy:
            case AutoFieldModelType.XPosition:
            case AutoFieldModelType.YPosition:
            case AutoFieldModelType.ZPosition:
                let autoFeatureField = featureField as AutoFeatureField;
                let autoTemplateField = templateField as AutoField;
                autoFeatureField.modelType = autoTemplateField.modelType;
                autoFeatureField.value = fieldValue;
                autoFeatureField.displayValue = autofieldDisplayValueDict[fieldValue] || fieldValue;
                autoFeatureField.unit = autoTemplateField.unit;
                autoFeatureField.isUTC = false;
                break;

            case AutoFieldModelType.CreationDateTime:
            case AutoFieldModelType.UpdateDateTime:
                let autoFeatureField2 = featureField as AutoFeatureField;
                autoFeatureField2.value = fieldValue;
                autoFeatureField2.isUTC = false;
                autoFeatureField2.modelType = featureField.type;
                break;

            case FieldType.Choice:
                let choiceFeatureField = featureField as ChoiceFeatureField;
                let choiceTemplateField = templateField as ChoiceField;
                choiceFeatureField.value = fieldValue ? fieldValue.toString().split('|') : [];
                choiceFeatureField.options = choiceTemplateField.values.map(value => value.text); // no need for default flag (yet)
                choiceFeatureField.allowMultiSelect = choiceTemplateField.allowMultiSelect;
                choiceFeatureField.allowNonDomainValues = choiceTemplateField.allowNonDomainValues;
                choiceFeatureField.maxLength = choiceTemplateField.length;
                break;

            case FieldType.CodedChoice:
                let codedChoiceTemplateField = templateField as CodedChoiceField;
                let codedChoiceFeatureField = featureField as CodedChoiceFeatureField;
                codedChoiceFeatureField.value = fieldValue;
                codedChoiceFeatureField.options = codedChoiceTemplateField.values.map(value => ({
                    code: value.code,
                    description: value.description
                }));
                break;

            case FieldType.Barcode:
                let barcodeFeatureField = featureField as BarcodeFeatureField;
                let barcodeTemplateField = templateField as BarcodeField;
                barcodeFeatureField.value = fieldValue;
                barcodeFeatureField.maxLength = barcodeTemplateField.length;
                break;

            case FieldType.YesNo:
                let yesNoFeatureField = featureField as YesNoFeatureField;
                yesNoFeatureField.value = fieldValue;
                break;
        }

        return featureField;
    }
}

export const autofieldDisplayValueDict: { [key: string]: string } = {
    CodeCorrected: 'Code Corrected',
    RtxCodeCorrected: 'RTX Code Corrected',
    ProcessingCodeCorrected: 'Processing Code Corrected',
    RtxCarrierCorrected: 'RTX Carrier Corrected',
    CarrierCorrected: 'Carrier Corrected',
    ProcessingCarrierCorrected: 'Processing Carrier Corrected',
    Rtx: 'RTX'
};
